import React, { FC, ReactNode } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
} from '@material-ui/core'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import { Link } from '@src/components/Link'
import ModalSkeleton from './ModalSkeleton'
import HandleMarkup from '@src/components/HandleMarkup'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@utils/messages'
import { SharedList } from '@src/types/graphql-types'
import { orderCenterRoutes } from '@src/routes'
import { useQuickOrder } from '@src/components/QuickOrder/utils/quickOrderContext'
import { MODALTYPE } from './enum'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'
import {
  DejaNewEventCategory as eventCategory,
  DejaNewEventAction as eventAction,
  DejaNewEventLabel as eventLabel,
} from '@utils/analytics/enums'
import { lineClamp } from '@src/styles/utils/lineClamp'
import clsx from 'clsx'
import {
  SharedListDetailFragment,
  SharedListFragment,
  SharedListItemFragment,
  useGetSharedListQuery,
} from '@src/queries/SharedListQueries.generated'
import LoadingBox from '@src/components/LoadingBox'
import ListError from '@src/components/ListError'
import { getListName } from '@src/routes/SharedList/utils'

//TODO: consider breaking into different files
//TODO: make styles reusable with RecentOrdersModal.tsx
const LISTS_QUANTITY_DEFAULT = 1

const useStyles = makeStyles((theme: Theme) => {
  return {
    skuCell: {
      alignItems: 'center',
      whiteSpace: 'nowrap',
      padding: theme.spacing(0, 0, 0, 1),
      width: '30%',
      '& > label': {
        alignItems: 'center',
        verticalAlign: 'middle',
      },
      '& > label > span': {
        marginTop: 0,
        lineHeight: 1.46,
      },
    },
    name: {
      ...lineClamp(2),
    },
    tableContainer: {
      maxHeight: '14rem',
      marginBottom: theme.spacing(2),
    },
    tableHeadCell: {
      paddingLeft: theme.spacing(7),
      width: '15rem',
    },
    tableHeadRow: {
      whiteSpace: 'nowrap',
      fontSize: theme.typography.pxToRem(14),
    },
    tableRow: {
      '& .MuiTableCell-body': {
        border: 0,
        verticalAlign: 'baseline',
      },
    },
    checkboxSpacingAdjustment: {
      position: 'relative',
      top: theme.spacing(0.75),
    },
    loadingBox: {
      backgroundImage:
        'linear-gradient(to right, #cfcfcf, #e0e0e0 25%, #f9f9f9, #e0e0e0 75%, #cfcfcf)',
      animationDuration: '2s',
      animationDelay: '0s',
      animationTimeFunction: 'ease-in',
    },
    emptyListNotice: {
      marginBottom: theme.spacing(6),
    },
  }
})

const ListsModalTitle: FC<{
  list: SharedListFragment | SharedListDetailFragment
}> = ({ list }) => {
  const intl = useIntl()
  const listName = getListName(intl.formatMessage)(list)

  return (
    <>
      {intl.formatMessage(messages.LISTS_QUICK_ORDER_MODAL_TITLE, {
        listName,
      })}
    </>
  )
}

const ItemRow: FC<{
  index: number
  name: string
  number: string
}> = ({ index, name, number }) => {
  const classes = useStyles()
  const { pendingItems, updatePendingItems } = useQuickOrder()

  const isChecked = pendingItems.some((item) => item.id === index)

  const handleCheck = () => {
    updatePendingItems(index, number, LISTS_QUANTITY_DEFAULT)
  }

  return (
    <>
      <TableRow classes={{ root: classes.tableRow }}>
        <TableCell className={classes.skuCell}>
          <LiquidCheckbox
            checked={isChecked}
            onChange={() => handleCheck()}
            label={
              <Typography variant="body2" component="span">
                {number}
              </Typography>
            }
          />
        </TableCell>
        <TableCell>
          <HandleMarkup
            className={clsx(classes.name, classes.checkboxSpacingAdjustment)}
            value={name}
          />
        </TableCell>
      </TableRow>
    </>
  )
}

const ListItemsTable: FC<{ children: ReactNode }> = ({ children }) => {
  const classes = useStyles()

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell classes={{ root: classes.tableHeadCell }}>
                <FormattedMessage {...messages.SKU} />
              </TableCell>
              <TableCell>
                <FormattedMessage {...messages.NAME} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

const getAnalyticsModalTitle = (intl, listName) => {
  return (
    `${intl.formatMessage(messages.LISTS_QUICK_ORDER_MODAL_TITLE, {
      listName,
    })}` || ''
  )
}

const ListsModalContent: FC<{
  listId: string
  listItems: SharedListItemFragment[]
  detailTitleForAnalytics: string
}> = ({ listId, listItems, detailTitleForAnalytics }) => {
  const classes = useStyles()

  return (
    <>
      {listItems.length > 0 ? (
        <ListItemsTable>
          {listItems.map((item, index) => {
            return (
              <ItemRow
                key={index}
                index={index}
                name={item.material.name || ''}
                number={item.material.number}
              />
            )
          })}
        </ListItemsTable>
      ) : (
        <div className={classes.emptyListNotice}>
          <Typography>
            <FormattedMessage {...messages.LIST_IS_EMPTY} />
          </Typography>
        </div>
      )}
      <Link href={orderCenterRoutes.listDetail(listId)}>
        {/* jsx-a11y rules disabled because href is implicitly passed to anchor */}
        {}
        <a
          onClick={() => {
            sendCommonDetailClickEvent(
              {
                event: 'quick_order_widget_interaction',
                action: 'visit list details',
                detail: detailTitleForAnalytics,
                section: 'add to quick order',
                component: 'modal',
                elementType: 'link',
                elementText: 'go to lists',
                linkUrl: `${orderCenterRoutes.listDetail(listId)}`,
                coreEvent: 'no',
              },
              {
                eventCategory: eventCategory.QuickOrderPage,
                eventAction: eventAction.Lists,
                eventLabel: eventLabel.ModalGoToLists,
                eventInteractionType: 0,
              }
            )
          }}
        >
          <FormattedMessage {...messages.GO_TO_LIST} />
          {' >'}
        </a>
      </Link>
    </>
  )
}

const ListsModalActions: FC<{
  listItems: SharedListItemFragment[]
  onClose: () => void
  detailTitleForAnalytics: string
}> = ({ listItems, onClose, detailTitleForAnalytics }) => {
  const { pendingItems, setAddItems, setPendingItems } = useQuickOrder()

  const itemsSelected = pendingItems.length > 0
  const hasListItems = listItems.length > 0

  const handleAddSelectedClick = () => {
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'add selected list items',
        detail: detailTitleForAnalytics,
        section: 'add to quick order',
        component: 'modal',
        elementType: 'button',
        elementText: 'add selected',
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.Lists,
        eventLabel: eventLabel.ModalAddSelected,
        eventInteractionType: 0,
      }
    )
    setAddItems(true)
    onClose()
  }

  const handleAddAllClick = () => {
    sendCommonDetailClickEvent(
      {
        event: 'quick_order_widget_interaction',
        action: 'add selected list items',
        detail: detailTitleForAnalytics,
        section: 'add to quick order',
        component: 'modal',
        elementType: 'button',
        elementText: 'add all',
        coreEvent: 'no',
      },
      {
        eventCategory: eventCategory.QuickOrderPage,
        eventAction: eventAction.Lists,
        eventLabel: eventLabel.ModalAddAll,
        eventInteractionType: 0,
      }
    )
    setPendingItems(
      listItems.map((item, index) => {
        return {
          id: index,
          materialNumber: item.material.number,
          quantity: LISTS_QUANTITY_DEFAULT,
        }
      })
    )
    setAddItems(true)
    onClose()
  }

  return (
    <>
      <Button
        color="primary"
        disabled={!itemsSelected}
        onClick={() => handleAddSelectedClick()}
        size="large"
        variant="outlined"
      >
        <FormattedMessage {...messages.ADD_SELECTED} />
      </Button>
      <Button
        color="primary"
        onClick={() => handleAddAllClick()}
        disabled={!hasListItems}
        size="large"
        variant="contained"
      >
        <FormattedMessage {...messages.ADD_ALL} />
      </Button>
    </>
  )
}

const ListsModal: FC<{
  onClose: () => void
  open: boolean
  list: SharedList
}> = ({ onClose, open, list }) => {
  const classes = useStyles()
  const intl = useIntl()
  const { listId } = list
  const { data, loading, error } = useGetSharedListQuery({
    fetchPolicy: 'network-only',
    variables: { listId, showPricing: false },
    ssr: false,
  })
  const detailTitleForAnalytics = getAnalyticsModalTitle(intl, list.name)

  const renderModalLoadingContent = () => (
    <ListItemsTable>
      {Array(4)
        .fill({})
        .map((_, index) => (
          <TableRow classes={{ root: classes.tableRow }} key={index}>
            <TableCell className={classes.skuCell}>
              <LoadingBox className={classes.loadingBox} height={24} />
            </TableCell>
            <TableCell>
              <LoadingBox className={classes.loadingBox} height={24} />
            </TableCell>
            <TableCell>
              <LoadingBox className={classes.loadingBox} height={24} />
            </TableCell>
          </TableRow>
        ))}
    </ListItemsTable>
  )

  const renderContent = () => {
    if (loading) return renderModalLoadingContent()
    if (error || !data || !data.getSharedList) return <ListError />
    return (
      <ListsModalContent
        listId={listId}
        listItems={data?.getSharedList?.sharedListItems || []}
        detailTitleForAnalytics={detailTitleForAnalytics}
      />
    )
  }

  return (
    <ModalSkeleton
      type={MODALTYPE.ListsModal}
      actions={
        <ListsModalActions
          listItems={data?.getSharedList?.sharedListItems || []}
          onClose={() => onClose()}
          detailTitleForAnalytics={detailTitleForAnalytics}
        />
      }
      content={renderContent()}
      onClose={() => onClose()}
      open={open}
      title={<ListsModalTitle list={list} />}
    />
  )
}

export default ListsModal
